import { useCallback, useState } from 'react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import styled, { css } from 'styled-components'
import { useDispatch } from 'react-redux'

import ENV_VARS from '@/config'
import { Session } from '@/models/session'
import { breakpoints, colors } from '@/styles'
import useRole from '@/hooks/use-role'
import { Button } from '@/components/button'
import { WarningIconTriangle } from '@/components/icons'
import useEventListener from '@/components/use-event-listener'
import useMediaQuery from '@/components/use-media-query'
import { showWarningModal } from '@/components/warning-modal'

const { DASHBOARD_URL } = ENV_VARS

const HeaderMenu = dynamic(() => import('../header-menu'), { ssr: false })

const RightNav = styled.nav`
  flex: 1 0 165px;
  min-width: 0;
  @media (max-width: 900px) {
    flex: 1 0 245px;
  }
  @media (max-width: ${breakpoints.ipadMiniMax}) {
    flex: 1 0 165px;
  }
`

const RightContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 24px;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    gap: 16px;
  }
`

const HeaderButton = styled.button<{ isScrolling: boolean }>`
  font-family: Inter, sans-serif;
  font-size: 16px;
  line-height: 20px;
  font-weight: 600;
  color: ${props => (props.isScrolling ? colors.grey08 : colors.white)};
  text-align: right;
  background: transparent;
  cursor: pointer;
  border: none;
  padding: 8px;
  margin: 0;
  white-space: nowrap;
  text-decoration: none;
  display: flex;
  justify-content: center;
  align-items: center;

  :hover {
    font-weight: 700;
  }
`

const PostListingButton = styled(HeaderButton)`
  color: ${colors.grey08};
  flex-shrink: 0;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 12px;
    line-height: 17px;
    width: 98px;
  }
`

const PricingButton = styled(HeaderButton)`
  color: ${colors.grey08};
  flex-shrink: 0;

  @media screen and (max-width: 980px) {
    display: none;
  }
`

const customButtonStyle = css`
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    height: 40px;
    font-weight: 700;
    font-size: 12px;
    line-height: 16px;
    padding: 12px;
    min-width: unset;
  }
`

interface Props {
  hasSession: boolean
  session: Session
  nav?: boolean
  showSearch?: boolean
}

const LandlordHeader: React.FC<Props> = ({ hasSession, session }) => {
  const { t } = useTranslation(['translation', 'landlords'])
  const [isScrolling, setIsScrolling] = useState(false)
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const dispatch = useDispatch()
  const router = useRouter()
  const logout = () => dispatch.auth.logout()
  const role = useRole()
  const isLandlord = role === 'landlord'
  const isTenant = role === 'tenant'
  const avatar = session.avatar?.aws_s3_key
  const offset = displayMobile ? 5 : 61
  const handleClick = () => {
    if (isTenant) {
      showWarningModal({
        title: t(
          'r.landlord.view.logged_in_as_renter.title',
          "Oops! It looks like you're logged in as a renter."
        ),
        message: t(
          'r.landlord.view.logged_in_as_renter.text',
          'To access landlord-specific features, please log out and sign up for a landlord account or log in with your landlord account details.'
        ),
        icon: WarningIconTriangle,
        iconBgColor: '#FCF5D7',
        button: {
          text: t('common.log_out', 'Log Out'),
          onClick: logout
        }
      })
    } else {
      router.push(
        isLandlord ? `${DASHBOARD_URL}/listing/create` : '/post-a-listing'
      )
    }
  }

  const onScroll = useCallback(() => {
    window.pageYOffset < offset ? setIsScrolling(false) : setIsScrolling(true)
  }, [offset])

  useEventListener('scroll', onScroll, {
    enabled: true
  })

  return (
    <>
      <RightNav>
        <RightContent>
          <Link href="/pricing" passHref legacyBehavior>
            <PricingButton
              as="a"
              id="menu-pricing-link"
              isScrolling={isScrolling}
            >
              {t('b.header.view.pricing.button', 'Pricing')}
            </PricingButton>
          </Link>
          {isScrolling ? (
            <Button
              as="a"
              id="landlord-post-listing-click"
              theme="primary"
              onClick={handleClick}
              customStyle={customButtonStyle}
            >
              {t('landlords:Post a listing')}
            </Button>
          ) : (
            <PostListingButton
              onClick={handleClick}
              id="landlord-post-listing-click"
              isScrolling={isScrolling}
            >
              {t('landlords:Post a listing')}
            </PostListingButton>
          )}
          <HeaderMenu
            hasSession={hasSession}
            avatarUrl={avatar}
            showSearch={false}
          />
        </RightContent>
      </RightNav>
    </>
  )
}

export default LandlordHeader
